
import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";

import schoolLogo from "./zpa.png";
import defaultProfile from "./profileavartar.jpg";

/* ============================================================
   ZION PRAISE ACADEMY — OFFICIAL TEACHER DATA SHEET
   Brand: Black & Gold
   Format: Single-page A4 Landscape
   IMPORTANT: Email and initial password are intentionally
   included to help teachers access the school portal.
============================================================ */

const COLORS = {
  black: "#111111",
  blackSoft: "#242424",
  gold: "#C9A227",
  goldLight: "#F7F0D8",
  white: "#FFFFFF",
  background: "#F8F8F6",
  gray: "#666666",
  grayLight: "#E3E3E3",
  grayDark: "#333333",
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 22,
    paddingBottom: 32,
    paddingHorizontal: 25,
    backgroundColor: COLORS.white,
    fontFamily: "Helvetica",
    color: COLORS.black,
  },

  header: {
    height: 76,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: COLORS.background,
    borderBottomWidth: 3,
    borderBottomColor: COLORS.gold,
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },

  logo: {
    width: 57,
    height: 57,
    objectFit: "contain",
  },

  headerInfo: {
    flex: 1,
    marginLeft: 12,
  },

  schoolName: {
    fontSize: 15,
    fontFamily: "Helvetica-Bold",
    color: COLORS.black,
    marginBottom: 4,
  },

  schoolSubtitle: {
    fontSize: 7.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.gold,
    marginBottom: 4,
  },

  headerText: {
    fontSize: 7,
    color: COLORS.gray,
    marginBottom: 2,
  },

  titleBox: {
    width: 153,
    paddingLeft: 8,
    alignItems: "flex-end",
  },

  documentTitle: {
    fontSize: 9,
    lineHeight: 1.3,
    fontFamily: "Helvetica-Bold",
    color: COLORS.black,
    textAlign: "right",
  },

  goldRule: {
    width: 70,
    height: 2,
    backgroundColor: COLORS.gold,
    marginTop: 5,
    marginBottom: 5,
  },

  documentSubTitle: {
    fontSize: 7,
    color: COLORS.gray,
    textAlign: "right",
  },

  loginNotice: {
    backgroundColor: COLORS.goldLight,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    paddingVertical: 5,
    paddingHorizontal: 8,
    marginBottom: 9,
  },

  loginNoticeText: {
    fontSize: 7.2,
    color: COLORS.blackSoft,
    lineHeight: 1.35,
  },

  mainRow: {
    flexDirection: "row",
    alignItems: "flex-start",
  },

  profileCard: {
    width: 196,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderColor: COLORS.grayLight,
    borderTopWidth: 3,
    borderTopColor: COLORS.gold,
    padding: 9,
    marginRight: 10,
  },

  profileTop: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },

  profileImage: {
    width: 57,
    height: 57,
    borderRadius: 28,
    objectFit: "cover",
    borderWidth: 2,
    borderColor: COLORS.gold,
  },

  profileHeading: {
    flex: 1,
    marginLeft: 8,
  },

  teacherName: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: COLORS.black,
    lineHeight: 1.3,
    marginBottom: 3,
  },

  teacherRole: {
    fontSize: 7,
    color: COLORS.gray,
  },

  infoRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.grayLight,
  },

  infoLabel: {
    width: 66,
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    color: COLORS.gray,
  },

  infoValue: {
    flex: 1,
    fontSize: 7,
    lineHeight: 1.3,
    color: COLORS.black,
  },

  passwordValue: {
    flex: 1,
    fontSize: 7,
    lineHeight: 1.3,
    fontFamily: "Helvetica-Bold",
    color: COLORS.black,
  },

  rightContent: {
    flex: 1,
  },

  sectionHeader: {
    backgroundColor: COLORS.black,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.gold,
    paddingVertical: 5,
    paddingHorizontal: 7,
    color: COLORS.white,
    fontSize: 7.8,
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
  },

  card: {
    borderWidth: 0.7,
    borderColor: COLORS.grayLight,
    paddingHorizontal: 8,
    paddingVertical: 6,
    marginBottom: 8,
  },

  assignmentRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 5,
  },

  assignmentLabel: {
    width: 91,
    fontSize: 7.2,
    fontFamily: "Helvetica-Bold",
    color: COLORS.grayDark,
  },

  assignmentValue: {
    flex: 1,
    fontSize: 7.2,
    lineHeight: 1.3,
    color: COLORS.black,
  },

  regulationGrid: {
    flexDirection: "row",
  },

  regulationColumn: {
    flex: 1,
    paddingRight: 8,
  },

  regulationText: {
    fontSize: 7,
    color: COLORS.grayDark,
    lineHeight: 1.35,
    marginBottom: 4,
  },

  authorization: {
    marginTop: 1,
    paddingHorizontal: 8,
    paddingTop: 6,
    paddingBottom: 5,
    backgroundColor: COLORS.background,
    borderTopWidth: 2,
    borderTopColor: COLORS.gold,
  },

  authorizationText: {
    fontSize: 7,
    color: COLORS.grayDark,
    lineHeight: 1.35,
  },

  signatureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 18,
    paddingHorizontal: 8,
  },

  signatureBox: {
    width: "38%",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.black,
    paddingTop: 4,
  },

  signatureText: {
    fontSize: 7,
    color: COLORS.grayDark,
    textAlign: "center",
  },

  footer: {
    position: "absolute",
    bottom: 13,
    left: 25,
    right: 25,
    paddingTop: 5,
    borderTopWidth: 0.8,
    borderTopColor: COLORS.grayLight,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },

  footerText: {
    fontSize: 6.5,
    color: COLORS.gray,
  },

  footerEmphasis: {
    fontSize: 6.5,
    fontFamily: "Helvetica-Bold",
    color: COLORS.blackSoft,
  },
});

/* Safely format values for PDF text. */
const displayValue = (value, fallback = "N/A") => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    const formatted = value
      .map((item) => {
        if (item === null || item === undefined) return "";
        if (typeof item === "object") {
          return (
            item.name ||
            item.title ||
            item.subject_name ||
            item.grade_name ||
            item.full_name ||
            ""
          );
        }
        return String(item);
      })
      .filter(Boolean)
      .join(", ");

    return formatted || fallback;
  }

  if (typeof value === "object") {
    return (
      value.name ||
      value.title ||
      value.full_name ||
      value.email ||
      value.label ||
      fallback
    );
  }

  return String(value);
};

const TeacherDataPDF = ({
  teacher = {},
  divisionName,
  subjectNames,
  gradeNames,
}) => {
  const teacherName = displayValue(
    teacher?.full_name,
    "Teacher Name Not Available"
  );

  const profileImage =
    teacher?.profile_image_url || defaultProfile;

  const email = displayValue(teacher?.email);
  const initialPassword = displayValue(
    teacher?.initial_password
  );

  return (
    <Document
      title={`Teacher Data Sheet - ${teacherName}`}
      author="Zion Praise Academy"
      subject="Official Teacher Data and Login Information"
      creator="Zion Praise Academy School System"
    >
      <Page
        size="A4"
        orientation="landscape"
        style={styles.page}
        wrap={false}
      >
        {/* SCHOOL HEADER */}
        <View style={styles.header}>
          <Image src={schoolLogo} style={styles.logo} />

          <View style={styles.headerInfo}>
            <Text style={styles.schoolName}>
              ZION PRAISE ACADEMY
            </Text>

            <Text style={styles.schoolSubtitle}>
              SENIOR HIGH SCHOOL • SCHOOL SYSTEM
            </Text>

            <Text style={styles.headerText}>
              Cowfield 1st Junction, Duport Road,
              Paynesville, Monrovia, Liberia
            </Text>

            <Text style={styles.headerText}>
              School Portal: https://zionpraiseacademy.com
            </Text>
          </View>

          <View style={styles.titleBox}>
            <Text style={styles.documentTitle}>
              OFFICIAL TEACHER{"\n"}DATA SHEET
            </Text>

            <View style={styles.goldRule} />

            <Text style={styles.documentSubTitle}>
              Academic Staff Record
            </Text>
          </View>
        </View>

        {/* LOGIN INSTRUCTIONS — KEEP EMAIL AND PASSWORD */}
        <View style={styles.loginNotice}>
          <Text style={styles.loginNoticeText}>
            PORTAL LOGIN INSTRUCTIONS: Use the email address
            and initial password shown in the teacher profile
            to sign in at https://zionpraiseacademy.com.
            Keep these credentials private and change the
            initial password after signing in, if the portal
            provides that option.
          </Text>
        </View>

        {/* MAIN CONTENT */}
        <View style={styles.mainRow}>
          {/* TEACHER PROFILE */}
          <View style={styles.profileCard}>
            <View style={styles.profileTop}>
              <Image
                src={profileImage}
                style={styles.profileImage}
              />

              <View style={styles.profileHeading}>
                <Text style={styles.teacherName}>
                  {teacherName}
                </Text>

                <Text style={styles.teacherRole}>
                  Registered Teaching Staff
                </Text>
              </View>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>
                Qualification
              </Text>
              <Text style={styles.infoValue}>
                {displayValue(teacher?.qualification)}
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Email</Text>
              <Text style={styles.infoValue}>
                {email}
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>
                Initial Password
              </Text>
              <Text style={styles.passwordValue}>
                {initialPassword}
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Contact</Text>
              <Text style={styles.infoValue}>
                {displayValue(teacher?.contact)}
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>Salary</Text>
              <Text style={styles.infoValue}>
                {displayValue(teacher?.salary)}
              </Text>
            </View>

            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>
                Date Added
              </Text>
              <Text style={styles.infoValue}>
                {displayValue(teacher?.date_add)}
              </Text>
            </View>
          </View>

          {/* ASSIGNMENTS AND REGULATIONS */}
          <View style={styles.rightContent}>
            <Text style={styles.sectionHeader}>
              TEACHING ASSIGNMENTS
            </Text>

            <View style={styles.card}>
              <View style={styles.assignmentRow}>
                <Text style={styles.assignmentLabel}>
                  Division
                </Text>
                <Text style={styles.assignmentValue}>
                  {displayValue(divisionName)}
                </Text>
              </View>

              <View style={styles.assignmentRow}>
                <Text style={styles.assignmentLabel}>
                  Subjects / Courses
                </Text>
                <Text style={styles.assignmentValue}>
                  {displayValue(
                    subjectNames,
                    "No subjects assigned"
                  )}
                </Text>
              </View>

              <View style={styles.assignmentRow}>
                <Text style={styles.assignmentLabel}>
                  Grade Classes
                </Text>
                <Text style={styles.assignmentValue}>
                  {displayValue(
                    gradeNames,
                    "No grade classes assigned"
                  )}
                </Text>
              </View>

              <View style={{ flexDirection: "row" }}>
                <Text style={styles.assignmentLabel}>
                  Dependent Student
                </Text>
                <Text style={styles.assignmentValue}>
                  {displayValue(
                    teacher?.dependent_student,
                    "None"
                  )}
                </Text>
              </View>
            </View>

            <Text style={styles.sectionHeader}>
              TEACHER RULES AND REGULATIONS
            </Text>

            <View style={styles.card}>
              <View style={styles.regulationGrid}>
                <View style={styles.regulationColumn}>
                  <Text style={styles.regulationText}>
                    1. Be punctual and attend school regularly.
                  </Text>

                  <Text style={styles.regulationText}>
                    2. Prepare and deliver effective lessons.
                  </Text>

                  <Text style={styles.regulationText}>
                    3. Respect students, parents, and colleagues.
                  </Text>

                  <Text style={styles.regulationText}>
                    4. Maintain appropriate professional boundaries.
                  </Text>

                  <Text style={styles.regulationText}>
                    5. Protect school property and resources.
                  </Text>
                </View>

                <View style={styles.regulationColumn}>
                  <Text style={styles.regulationText}>
                    6. Perform duties with commitment and integrity.
                  </Text>

                  <Text style={styles.regulationText}>
                    7. Be a positive role model for students.
                  </Text>

                  <Text style={styles.regulationText}>
                    8. Maintain a clean learning environment.
                  </Text>

                  <Text style={styles.regulationText}>
                    9. Maintain a professional appearance.
                  </Text>

                  <Text style={styles.regulationText}>
                    10. Follow school policies and communication procedures.
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* AUTHORIZATION AND SIGNATURES */}
        <View style={styles.authorization}>
          <Text style={styles.sectionHeader}>
            AUTHORIZATION AND ACKNOWLEDGMENT
          </Text>

          <Text style={styles.authorizationText}>
            I acknowledge receipt of this teacher data sheet,
            including the portal login instructions. I agree
            to follow school policies, protect confidential
            information, and perform my duties professionally.
          </Text>

          <View style={styles.signatureRow}>
            <View style={styles.signatureBox}>
              <Text style={styles.signatureText}>
                Teacher Signature / Date
              </Text>
            </View>

            <View style={styles.signatureBox}>
              <Text style={styles.signatureText}>
                Authorized Administrator / Date
              </Text>
            </View>
          </View>
        </View>

        {/* FIXED FOOTER */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerEmphasis}>
            ZION PRAISE ACADEMY • SENIOR HIGH SCHOOL
          </Text>

          <Text style={styles.footerText}>
            CONFIDENTIAL STAFF RECORD • Handle login credentials securely
          </Text>

          <Text style={styles.footerText}>
            Powered by Polu Integrated School Management System
          </Text>
        </View>
      </Page>
    </Document>
  );
};

export default TeacherDataPDF;
